import { DEFAULT_DISPLAY_PROFILE, DISPLAY_PROFILES, type DisplayProfile } from "./displayProfile";

export interface DisplayViewport {
  width: number;
  height: number;
  devicePixelRatio: number;
  visualScale: number;
  screenWidth: number;
  screenHeight: number;
}

export type DisplayWarningCode = "browser-zoom" | "viewport-loss" | "non-standard-aspect-ratio" | "distant-profile";

export interface DisplayWarning {
  code: DisplayWarningCode;
  message: string;
}

export interface ResolvedDisplayProfile {
  profile: DisplayProfile;
  distance: number;
  stage: { width: number; height: number };
  effectiveResolution: { width: number; height: number };
  renderPixelRatio: number;
  warnings: DisplayWarning[];
}

const TARGET_ASPECT_RATIO = 16 / 9;

export function resolveDisplayProfile(viewport: DisplayViewport): ResolvedDisplayProfile {
  if (viewport.width <= 0 || viewport.height <= 0) {
    return {
      profile: DEFAULT_DISPLAY_PROFILE,
      distance: 0,
      stage: { width: 0, height: 0 },
      effectiveResolution: { width: 0, height: 0 },
      renderPixelRatio: Math.max(1, viewport.devicePixelRatio || 1),
      warnings: []
    };
  }

  const effectiveResolution = getEffectiveResolution(viewport);
  const profile = DISPLAY_PROFILES.reduce((closest, candidate) =>
    profileDistance(effectiveResolution, candidate) < profileDistance(effectiveResolution, closest) ? candidate : closest
  );
  const distance = profileDistance(effectiveResolution, profile);
  const stage = fitStage(viewport.width, viewport.height);
  const warnings = collectWarnings(viewport, effectiveResolution, distance);

  return {
    profile,
    distance,
    stage,
    effectiveResolution,
    renderPixelRatio: Math.max(1, viewport.devicePixelRatio || 1),
    warnings
  };
}

function getEffectiveResolution(viewport: DisplayViewport): { width: number; height: number } {
  const pixelRatio = Math.max(1, viewport.devicePixelRatio || 1);
  return {
    width: Math.round(viewport.width * pixelRatio),
    height: Math.round(viewport.height * pixelRatio)
  };
}

function profileDistance(resolution: { width: number; height: number }, profile: DisplayProfile): number {
  return Math.hypot((resolution.width - profile.width) / profile.width, (resolution.height - profile.height) / profile.height);
}

function fitStage(width: number, height: number): { width: number; height: number } {
  const stageWidth = Math.min(width, Math.floor(height * TARGET_ASPECT_RATIO));
  return { width: stageWidth, height: Math.floor(stageWidth / TARGET_ASPECT_RATIO) };
}

function collectWarnings(viewport: DisplayViewport, effectiveResolution: { width: number; height: number }, distance: number): DisplayWarning[] {
  const warnings: DisplayWarning[] = [];
  if (Math.abs(viewport.visualScale - 1) > 0.01) {
    warnings.push({ code: "browser-zoom", message: "Browser Zoom Detected" });
  }
  if (viewport.screenWidth > 0 && viewport.screenHeight > 0 && (effectiveResolution.width < viewport.screenWidth * 0.98 || effectiveResolution.height < viewport.screenHeight * 0.98)) {
    warnings.push({ code: "viewport-loss", message: "检查浏览器缩放或 Overscan" });
  }
  if (Math.abs(viewport.width / viewport.height - TARGET_ASPECT_RATIO) / TARGET_ASPECT_RATIO > 0.02) {
    warnings.push({ code: "non-standard-aspect-ratio", message: "Viewport 不是标准 16:9" });
  }
  if (distance > 0.08) {
    warnings.push({ code: "distant-profile", message: "已使用最近 Display Profile" });
  }
  return warnings;
}
